import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { Bookmark, X, ExternalLink, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

export default function BaselinePanel({ teamId, teamName, currentPoints, onClose, isFacilitator, cloudName }) {
  const [baselines, setBaselines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [filterPoints, setFilterPoints] = useState(currentPoints || '');

  // Add form state
  const [addKey, setAddKey] = useState('');
  const [addSummary, setAddSummary] = useState('');
  const [addPoints, setAddPoints] = useState('');
  const [addNotes, setAddNotes] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.teams.baselines(teamId, filterPoints || undefined);
      setBaselines(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [teamId, filterPoints]);

  // When currentPoints changes (e.g. after reveal), sync filter
  useEffect(() => {
    if (currentPoints != null) setFilterPoints(currentPoints);
  }, [currentPoints]);

  const handleAdd = async () => {
    if (!addKey || !addPoints) return;
    setAdding(true);
    setAddError('');
    try {
      await api.teams.addBaseline(teamId, {
        jira_ticket_key: addKey.trim().toUpperCase(),
        jira_ticket_summary: addSummary.trim(),
        points: parseInt(addPoints),
        notes: addNotes.trim()
      });
      setAddKey(''); setAddSummary(''); setAddPoints(''); setAddNotes('');
      setShowAdd(false);
      await load();
    } catch (e) {
      setAddError(e.message);
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (baselineId) => {
    if (!window.confirm('Remove this baseline?')) return;
    try {
      await api.teams.deleteBaseline(teamId, baselineId);
      setBaselines(bs => bs.filter(b => b.id !== baselineId));
    } catch (e) {
      console.error(e);
    }
  };

  const POINT_VALUES = ['1', '2', '3', '5', '8'];

  return (
    <div className="baseline-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Bookmark size={16} style={{ color: 'var(--yellow)' }} />
          <span className="font-bold" style={{ color: 'var(--yellow)' }}>
            Baselines {teamName ? `— ${teamName}` : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isFacilitator && (
            <button
              className="btn btn-sm"
              style={{
                background: showAdd ? 'var(--accent-dim)' : 'transparent',
                color: showAdd ? 'var(--accent)' : 'var(--text-muted)',
                border: `1px solid ${showAdd ? 'var(--accent)' : 'var(--border)'}`,
              }}
              onClick={() => setShowAdd(s => !s)}
            >
              <Plus size={12} /> Add
            </button>
          )}
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Filter by points */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-xs text-muted">Filter:</span>
        <button
          className={`btn btn-sm ${!filterPoints ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilterPoints('')}
        >
          All
        </button>
        {POINT_VALUES.map(v => (
          <button
            key={v}
            className={`btn btn-sm ${filterPoints == v ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilterPoints(filterPoints == v ? '' : v)}
          >
            {v}
          </button>
        ))}
      </div>

      {/* Add baseline form */}
      {showAdd && isFacilitator && (
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--accent)',
          borderRadius: 'var(--radius-sm)', padding: 14, marginBottom: 12
        }}>
          <p className="text-sm font-bold mb-3" style={{ color: 'var(--accent)' }}>Add Baseline Reference</p>
          <div className="grid-2" style={{ gap: 8, marginBottom: 8 }}>
            <div>
              <label className="input-label">Ticket Key *</label>
              <input className="input" value={addKey} onChange={e => setAddKey(e.target.value)}
                placeholder="e.g. PROJ-123" style={{ marginTop: 4 }} />
            </div>
            <div>
              <label className="input-label">Story Points *</label>
              <div className="flex gap-1 mt-1 flex-wrap">
                {POINT_VALUES.map(v => (
                  <button key={v} className={`btn btn-sm ${addPoints == v ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setAddPoints(v)}>{v}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="input-group" style={{ marginBottom: 8 }}>
            <label className="input-label">Summary (optional)</label>
            <input className="input" value={addSummary} onChange={e => setAddSummary(e.target.value)}
              placeholder="Short description of the ticket" />
          </div>
          <div className="input-group" style={{ marginBottom: 10 }}>
            <label className="input-label">Notes (optional)</label>
            <input className="input" value={addNotes} onChange={e => setAddNotes(e.target.value)}
              placeholder="Why is this a good baseline? e.g. 'Classic 5-pointer'" />
          </div>
          {addError && <div className="toast toast-error mb-2" style={{ fontSize: 12 }}>{addError}</div>}
          <div className="flex gap-2">
            <button className="btn btn-secondary btn-sm" onClick={() => setShowAdd(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={handleAdd}
              disabled={adding || !addKey || !addPoints}>
              {adding ? 'Saving...' : 'Save Baseline'}
            </button>
          </div>
        </div>
      )}

      {loading && <p className="text-sm text-muted">Loading baselines...</p>}

      {!loading && baselines.length === 0 && (
        <p className="text-sm text-muted">
          {filterPoints
            ? `No baselines at ${filterPoints} points yet.`
            : 'No baselines for this team yet.'}
          {isFacilitator && ' Use the Add button to create one.'}
        </p>
      )}

      <div className="flex-col gap-2">
        {baselines.map(b => (
          <div key={b.id} style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)', padding: '10px 14px'
          }}>
            <div className="flex items-center gap-2 mb-1">
              <span className="badge badge-yellow font-mono">{b.jira_ticket_key}</span>
              <span className="badge badge-purple">{b.points} pts</span>
              <a
                href={`https://${cloudName || 'yoursite'}.atlassian.net/browse/${b.jira_ticket_key}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-ghost btn-icon btn-sm"
                style={{ marginLeft: 'auto', padding: '2px 4px' }}
                onClick={e => e.stopPropagation()}
              >
                <ExternalLink size={12} />
              </a>
              {isFacilitator && (
                <button
                  className="btn btn-ghost btn-icon btn-sm"
                  onClick={() => handleDelete(b.id)}
                  style={{ color: 'var(--red)', padding: '2px 4px' }}
                  title="Remove baseline"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
            {b.jira_ticket_summary && <p className="text-sm">{b.jira_ticket_summary}</p>}
            {b.notes && <p className="text-xs text-muted mt-1 font-mono">💡 {b.notes}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
